import keyRegistryData from '../config/keyRegistry.json';

// Handle both possible structures: { keys: [...] } or [...]
const ALL_KEYS = keyRegistryData.keys || keyRegistryData;

/**
 * Returns all registry keys applicable to the given language:
 * common keys (no `language` field) plus keys whose `language === lang`.
 * Language-specific keys for other languages are excluded.
 */
export function getKeysForLanguage(lang) {
  return ALL_KEYS.filter((k) => !k.language || k.language === lang);
}

/**
 * Maps a registry entry to the chip descriptor shape that QuickAddChips expects:
 *   { label, key, category, options?, value? }
 *
 * Two registry shapes become chips:
 *   1. Predetermined quick-add chips: `quickAddChip: true` with `generatedKey` +
 *      `generatedValue` (e.g. "verb conjugation", "separable verb"). These map to
 *      category 'quickAdd' with `key = generatedKey` and `value = generatedValue`.
 *   2. Regular chips: any entry flagged `chip: true` with its own `category`
 *      (plainText, restrictedDropdown, dictionaryReference). These carry through
 *      `options` for dropdowns.
 */
function toChipDescriptor(entry) {
  const label = entry.chipLabel || entry.key;

  if (entry.quickAddChip && entry.generatedKey) {
    return {
      label,
      key: entry.generatedKey,
      category: 'quickAdd',
      value: entry.generatedValue,
    };
  }

  const descriptor = {
    label,
    key: entry.key,
    category: entry.category,
  };
  if (entry.options) {
    descriptor.options = entry.options;
  }
  return descriptor;
}

/**
 * Returns the learner-facing key note for a given key, scoped to the language.
 *
 * Looks up the registry entry matching `key` within the keys applicable to
 * `lang` (common keys plus keys whose `language === lang`) and returns its
 * `keyNote` string when non-empty, otherwise `null`. Reads only the single
 * registry source (`keyRegistry.json`) — no per-entry duplication.
 */
export function getKeyNote(key, lang) {
  const entry = getKeysForLanguage(lang).find((k) => k.key === key);
  return entry && entry.keyNote ? entry.keyNote : null;
}

/**
 * Returns the quick-add chip descriptors for the given language, in registry
 * order, using the chip shape downstream render/save logic expects.
 */
export function getQuickAddChipsForLanguage(lang) {
  return getKeysForLanguage(lang)
    .filter((k) => k.chip === true)
    .map(toChipDescriptor);
}

export default ALL_KEYS;
